import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  commissionTransitionsFor, formatMinor, isCommissionFinal,
  type Commission, type CommissionMessage,
} from '@eprintsmart/shared';
import {
  postCommissionMessage, transitionCommission, watchCommissionMessages, watchCommissions,
} from '../services/commissions';

/**
 * Commissioned work, and the ability to step into one that has gone wrong.
 *
 * Staff can read every thread and post into it. That is a real privilege over
 * private correspondence, and it exists for one reason: when a customer and a
 * designer disagree, somebody has to be able to see what was actually said and
 * answer both of them in the same place. Every staff message is labelled as
 * such in the thread — nobody is impersonated.
 *
 * Ordered by what is waiting on someone. A commission nobody has to act on is
 * not work, and putting it at the top is how the ones that are get missed.
 */

const WAITING_ON: Record<string, string> = {
  ENQUIRY: 'designer',
  ACCEPTED: 'customer — payment',
  PAID: 'designer',
  DELIVERED: 'customer',
  REVISION: 'designer',
};

const STATUS_COLOUR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  ENQUIRY: 'warning',
  ACCEPTED: 'warning',
  PAID: 'default',
  DELIVERED: 'default',
  REVISION: 'warning',
  APPROVED: 'success',
  DECLINED: 'error',
  CANCELLED: 'error',
};

const AGE = (ms: number) => {
  const hours = Math.floor((Date.now() - Number(ms)) / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export function Commissions() {
  const [rows, setRows] = useState<Commission[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchCommissions(setRows), []);

  const counts = useMemo(() => ({
    open: rows.filter((r) => !isCommissionFinal(r.status)).length,
    // Anything untouched for two days with somebody waiting on it. The whole
    // point of a marketplace is that briefs get answered.
    stale: rows.filter(
      (r) => !isCommissionFinal(r.status) && Date.now() - Number(r.updatedAt) > 2 * 86_400_000,
    ).length,
  }), [rows]);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {counts.stale > 0 && (
        <Alert severity="warning">
          {counts.stale} commission{counts.stale === 1 ? ' has' : 's have'} sat untouched for more
          than two days with somebody waiting. Open the thread and chase whoever is holding it up.
        </Alert>
      )}

      <Box>
        <Typography variant="h6" gutterBottom>
          Commissions{counts.open > 0 && ` — ${counts.open} open`}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Commissioned work is private to the customer and the designer. You can read and write
          here so a dispute can be settled from what was actually said — anything you post is
          labelled as ePrintSmart.
        </Typography>

        <Paper variant="outlined">
          {rows.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                Nothing commissioned yet. Designers appear on /designers once they switch on
                &ldquo;Take commissions&rdquo; and describe a package.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>What</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Designer</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Waiting on</TableCell>
                  <TableCell align="right">Idle</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => (
                  <CommissionRow
                    key={c.id}
                    commission={c}
                    expanded={open === c.id}
                    busy={busy}
                    onToggle={() => setOpen(open === c.id ? null : c.id)}
                    onBusy={setBusy}
                    onError={setError}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}

function CommissionRow({
  commission, expanded, busy, onToggle, onBusy, onError,
}: {
  commission: Commission;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onBusy: (b: boolean) => void;
  onError: (e: string | null) => void;
}) {
  const [messages, setMessages] = useState<CommissionMessage[]>([]);
  const [draft, setDraft] = useState('');

  // Only while open. Watching every thread on the page would be a listener per
  // commission for conversations nobody is reading.
  useEffect(
    () => (expanded ? watchCommissionMessages(commission.id, setMessages) : undefined),
    [expanded, commission.id],
  );

  const act = async (fn: () => Promise<unknown>) => {
    onBusy(true);
    onError(null);
    try {
      await fn();
      setDraft('');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      onBusy(false);
    }
  };

  const moves = commissionTransitionsFor(commission.status, 'admin');

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={onToggle}>
            <ExpandMoreIcon fontSize="small"
              sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
          </IconButton>
        </TableCell>
        <TableCell>{commission.package.title}</TableCell>
        <TableCell>{commission.customerName}</TableCell>
        <TableCell>/artist/{commission.designerHandle}</TableCell>
        <TableCell align="right">{formatMinor(commission.priceMinor)}</TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {WAITING_ON[commission.status] ?? '—'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="caption"
            color={Date.now() - Number(commission.updatedAt) > 2 * 86_400_000
              ? 'warning.main' : 'text.secondary'}>
            {AGE(commission.updatedAt)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip size="small" label={commission.status.toLowerCase()}
            color={STATUS_COLOUR[commission.status] ?? 'default'} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box py={2} px={1}>
              <Typography variant="caption" color="text.secondary">The brief</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {commission.brief}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Conversation ({messages.length})
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', my: 1 }}>
                {messages.map((m) => (
                  <Box key={m.id} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {m.from} · {new Date(Number(m.at)).toLocaleString('en-IN')}
                      {m.attachments?.length ? ` · ${m.attachments.length} file(s)` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
                  </Box>
                ))}
                {messages.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Nothing said yet.</Typography>
                )}
              </Box>

              {!isCommissionFinal(commission.status) && (
                <>
                  <TextField
                    fullWidth multiline rows={2} size="small" value={draft}
                    placeholder="Write to both of them, as ePrintSmart…"
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="contained" disabled={busy || !draft.trim()}
                      onClick={() => void act(() => postCommissionMessage({
                        commissionId: commission.id, body: draft.trim(),
                      }))}>
                      Post
                    </Button>
                    {moves.map((t) => (
                      <Tooltip key={t.to} title="Sends the message above as the note, if written">
                        <span>
                          <Button size="small" disabled={busy}
                            color={t.to === 'CANCELLED' ? 'error' : 'primary'}
                            onClick={() => void act(() => transitionCommission({
                              commissionId: commission.id,
                              to: t.to,
                              ...(draft.trim() ? { note: draft.trim() } : {}),
                            }))}>
                            {t.label}
                          </Button>
                        </span>
                      </Tooltip>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
